import { CutType } from '@api/cut';
import { AppRoutes } from './routes';

import type { ColumnOptions } from '@components/tables/DataTable';
import type { ParsedCut } from '@/types/cuts';

export enum CutActivityColumn {
    CutDate = 'Cut Date',
    SyncDate = 'Sync Date',
    CutType = 'Cut Type',
    Product = 'Product',
    Company = 'Company',
    Reseller = 'Reseller',
    Location = 'Location',
    Manufacturer = 'Manufacturer',
    Coverage = 'Coverage',
    Status = 'Status',
    Model = 'Model',
    CutID = 'Cut ID',
    BatchID = 'Batch ID',
    CreditID = 'Credit ID',
    EntryCode = 'Entry Code',
    Select = 'Select',
    LocationID = 'Location ID',
    Country = 'Country',
    EmployeeEmail = 'Employee Email',
    Category = 'Category',
    StationID = 'System ID',
    SerialNum = 'Serial #',
}

const CUT_TYPE_COLORS = {
    [CutType.Successful]: 'green',
    [CutType.Warranty]: 'blue',
    [CutType.FailedCut]: 'red',
    [CutType.DiagnosticCut]: 'orange',
};

export const CUT_ACTIVITY_COLUMNS: ColumnOptions<ParsedCut>[] = [
    {
        field: 'cutDate',
        headerName: CutActivityColumn.CutDate,
        minWidth: 175,
        sortable: true,
        getCellLink: (row) => `${AppRoutes.CutHistory}?id=${row.dbCutId}`,
        tooltip: 'Click to view history',
    },
    {
        field: 'syncDate',
        headerName: CutActivityColumn.SyncDate,
        minWidth: 175,
        getCellLink: (row) => `${AppRoutes.CutHistory}?id=${row.dbCutId}`,
        tooltip: 'Click to view history',
    },
    {
        field: 'cutId',
        headerName: CutActivityColumn.CutID,
        minWidth: 150,
        getCellLink: (row) => `${AppRoutes.CutHistory}?id=${row.dbCutId}`,
        tooltip: 'Click to view history',
    },
    {
        field: 'batchId',
        headerName: CutActivityColumn.BatchID,
        minWidth: 150,
        getCellLink: (row) => `${AppRoutes.Activity}?search=${row.batchId}&type=BatchID&auto=`,
        tooltip: 'Click to view all results for this Batch ID',
    },
    {
        field: 'invoiceId',
        headerName: 'Invoice ID',
        minWidth: 150,
        getCellLink: (row) => `${AppRoutes.Activity}?search=${row.invoiceId}&type=InvoiceID&auto=`,
        tooltip: 'Click to view all results for this Invoice ID',
    },
    {
        field: 'creditId',
        headerName: CutActivityColumn.CreditID,
        minWidth: 150,
        getCellLink: (row) => `${AppRoutes.Activity}?search=${row.creditId}&type=CreditID&auto=`,
        tooltip: 'Click to view all results for this Credit ID',
    },
    {
        field: 'entryCode',
        headerName: CutActivityColumn.EntryCode,
        minWidth: 150,
    },
    {
        field: 'cutType',
        headerName: CutActivityColumn.CutType,
        minWidth: 150,
        getStyles: (row) => ({
            color: CUT_TYPE_COLORS[row!.cutType as keyof typeof CUT_TYPE_COLORS],
        }),
    },
    {
        field: 'category',
        headerName: CutActivityColumn.Category,
        minWidth: 150,
    },
    {
        field: 'product',
        headerName: CutActivityColumn.Product,
        minWidth: 150,
    },
    {
        field: 'company',
        headerName: CutActivityColumn.Company,
        minWidth: 200,
        getCellLink: (row) => `${AppRoutes.Organizations}?type=company&id=${row.companyIdRaw}`,
        tooltip: 'Click to manage company',
    },
    {
        field: 'reseller',
        headerName: CutActivityColumn.Reseller,
        minWidth: 200,
        getCellLink: (row) => `${AppRoutes.Organizations}?type=reseller&id=${row.resellerIdRaw}`,
        tooltip: 'Click to manage reseller',
    },
    {
        field: 'locationId',
        headerName: CutActivityColumn.LocationID,
        minWidth: 150,
        getCellLink: (row) => `${AppRoutes.Organizations}?type=location&id=${row.locationIdRaw}`,
        tooltip: 'Click to manage location',
    },
    {
        field: 'location',
        headerName: CutActivityColumn.Location,
        minWidth: 200,
        getCellLink: (row) => `${AppRoutes.Organizations}?type=location&id=${row.locationIdRaw}`,
        tooltip: 'Click to manage location',
    },
    {
        field: 'stationId',
        headerName: CutActivityColumn.StationID,
        minWidth: 150,
        getCellLink: (row) => (row.stationIdRaw ? `${AppRoutes.Organizations}?type=system&id=${row.stationIdRaw}` : ''),
        tooltip: 'Click to manage system',
    },
    {
        field: 'serialNum',
        headerName: CutActivityColumn.SerialNum,
        minWidth: 150,
        getCellLink: (row) => (row.stationIdRaw ? `${AppRoutes.Organizations}?type=system&id=${row.stationIdRaw}` : ''),
        tooltip: 'Click to manage system',
    },
    {
        field: 'manufacturer',
        headerName: CutActivityColumn.Manufacturer,
        minWidth: 150,
    },
    {
        field: 'model',
        headerName: CutActivityColumn.Model,
        minWidth: 150,
    },
    {
        field: 'coverage',
        headerName: CutActivityColumn.Coverage,
        minWidth: 150,
    },
    {
        field: 'cutStatus',
        headerName: CutActivityColumn.Status,
        minWidth: 150,
    },
    {
        field: 'country',
        headerName: CutActivityColumn.Country,
        minWidth: 150,
    },
    {
        field: 'employeeEmail',
        headerName: CutActivityColumn.EmployeeEmail,
        minWidth: 150,
    },
];

export enum SearchIDType {
    CutID = 'CutID',
    BatchID = 'BatchID',
    CreditID = 'CreditID',
    InvoiceID = 'InvoiceID',
    EntryCode = 'EntryCode',
}

export const TYPES = {
    [SearchIDType.CutID]: {
        validator: (value: string) => {
            // ? Any number of characters with a dash somewhere in between
            return /^.+-.+$/.test(value);
        },
        example: '4325-NiwYBDpl9',
        label: 'Cut ID',
    },
    [SearchIDType.BatchID]: {
        validator: (value: string) => {
            // ? {CompanyID}{S|WA|WD|P}{YY}-{MM}-{LocationID}-{MaterialID}
            return /^\d+(S|WA|WD|P)\d{2}-\d{2}-\d+-\d+$/.test(value);
        },
        example: '342WA22-04-8384-9',
        label: 'Batch ID',
    },
    [SearchIDType.CreditID]: {
        validator: (value: string) => {
            // ? {CompanyID}{C}{YY}-{MM}-{LocationID}-{MaterialID}
            return /^\d+C\d{2}-\d{2}-\d+-\d+$/.test(value);
        },
        example: '342C22-04-8384-9',
        label: 'Credit ID',
    },
    [SearchIDType.InvoiceID]: {
        validator: (value: string) => {
            // * A truncated BatchID
            // ? {CompanyID}{S|W|C|P}{YY}-{MM}
            return /^\d+(S|WA|WD|P)\d{2}-\d{2}$/.test(value);
        },
        example: '342WA22-04',
        label: 'Invoice ID',
    },
    [SearchIDType.EntryCode]: {
        validator: (value: string) => {
            // Entry codes are alphanumeric, typically uppercase; allow non-empty string
            return value.trim().length > 0;
        },
        example: 'ABC123',
        label: 'Entry Code',
    },
};
